import { useState, useEffect } from "react";
import { useLenis } from "lenis/react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, ArrowRight } from "lucide-react";
import { BRAND, NAV, NAV_MORE } from "../lib/site";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setMoreOpen(false);
    const el = document.getElementById(id);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -80 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        data-testid="main-nav"
        className={`fixed inset-x-0 top-0 z-[100] transition-all duration-500 ${
          scrolled ? "py-2" : "py-4"
        }`}
      >
        <div
          className={`mx-auto flex items-center justify-between px-5 transition-all duration-500 md:px-8 ${
            scrolled
              ? "mx-3 max-w-[1400px] rounded-full border border-neutral-200 bg-white/85 py-2.5 shadow-[0_10px_40px_-15px_rgba(0,0,0,0.25)] backdrop-blur-xl md:mx-auto"
              : "max-w-[1500px] bg-transparent py-2"
          }`}
        >
          <button onClick={() => go("home")} data-testid="nav-logo-link" className="flex items-center" data-cursor="view">
            <img src={BRAND.logo} alt="PHOTO PIXEL®" className="h-8 w-auto md:h-9" />
          </button>

          <nav className="hidden items-center gap-6 xl:flex">
            {NAV.map((item) => (
              <button
                key={item.id}
                onClick={() => go(item.id)}
                data-testid={`nav-link-${item.id}`}
                className="group relative overline text-pp-ink transition-colors hover:text-pp-red"
              >
                {item.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-pp-red transition-all duration-300 group-hover:w-full" />
              </button>
            ))}

            <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
              <button data-testid="nav-more" className="flex items-center gap-1 overline text-pp-ink transition-colors hover:text-pp-red">
                More <ChevronDown size={14} />
              </button>
              <AnimatePresence>
                {moreOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    className="absolute right-0 top-full w-52 border border-neutral-200 bg-white p-2 shadow-xl"
                  >
                    {NAV_MORE.map((m) => (
                      <button
                        key={m.id}
                        onClick={() => go(m.id)}
                        data-testid={`nav-more-${m.id}`}
                        className="block w-full px-3 py-2.5 text-left text-sm text-pp-ink transition-colors hover:bg-neutral-50 hover:text-pp-red"
                      >
                        {m.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </nav>

          <div className="flex items-center gap-3">
            <button
              onClick={() => go("locations")}
              data-testid="nav-visit-store"
              className="pp-btn hidden rounded-full bg-pp-red px-5 py-2.5 text-sm text-white hover:bg-pp-reddeep md:inline-flex"
              data-cursor="view"
            >
              Visit Store <ArrowRight size={16} />
            </button>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              className="inline-flex h-10 w-10 items-center justify-center border border-neutral-300 text-pp-ink xl:hidden"
              aria-label="Menu"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[99] overflow-y-auto bg-pp-black noise xl:hidden"
            data-testid="mobile-nav-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="flex min-h-full flex-col justify-center px-8 py-24">
              <img src={BRAND.logo} alt="PHOTO PIXEL®" className="mb-10 h-9 w-auto self-start bg-white p-2" />
              <nav className="flex flex-col gap-1">
                {[...NAV, ...NAV_MORE].map((item, i) => (
                  <motion.button
                    key={item.id}
                    onClick={() => go(item.id)}
                    data-testid={`mobile-nav-link-${item.id}`}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * i }}
                    className="text-left font-display text-3xl text-white transition-colors hover:text-pp-red"
                  >
                    {item.label}
                  </motion.button>
                ))}
              </nav>
              <button
                onClick={() => go("locations")}
                data-testid="mobile-visit-store"
                className="pp-btn mt-10 self-start rounded-full bg-pp-red px-6 py-3 text-white"
              >
                Visit Store <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
